import { perfilPorId, type IdPerfil, type PerfilDisponivel } from './perfis';
import { alcancePropostoDe } from './proposta';
import {
  ACOES_DO_SPRINT,
  MODULOS_DO_SISTEMA,
  acaoPorId,
  moduloEstaConstruido,
  moduloPorId,
  type AcaoDoSprint,
  type CaminhoConstruido,
  type IdAcao,
  type IdModulo,
  type ModuloDoSistema,
} from './sprint';
import { licencasDaTela } from './telas';

/**
 * A unica porta do modulo de acesso.
 *
 * Ela junta TRES coisas de origens diferentes e mantem cada uma etiquetada: a
 * tabela de perfis e citacao do guia, o administrador e decisao do dono, e a
 * matriz de alcance e proposta. Cada perfil sai daqui com a `origem` colada
 * nele, para a tela dizer de onde ele veio sem ninguem precisar abrir o codigo.
 *
 * NAO HA MAIS NOME DE PESSOA AQUI. Ate esta rodada o seletor do login oferecia
 * uma pessoa inventada por perfil, porque nao havia cadastro de usuario. Agora
 * ha: quem entra e uma pessoa de `org.usuario`, com credencial propria, e o
 * papel dela vem do banco. O que sobrou neste modulo e o que ele sempre foi de
 * verdade — a PROPOSTA de permissao, que continua sendo do dono.
 *
 * O QUE ELA CALCULA, E POR QUE ISSO NAO E DETALHE
 * O que um perfil NAO alcanca sai por DIFERENCA — nunca de uma segunda lista
 * escrita a mao. Uma lista de negativas mantida a parte envelhece calada: o
 * modulo novo nasce, entra no mapa, e continua ausente das duas listas ate
 * alguem reparar. Por diferenca, todo modulo e toda acao do Sprint 1 aparece
 * de um lado ou do outro, sempre, sem ninguem precisar lembrar.
 *
 * E a diferenca separa DOIS casos que nao podem virar um so: "fora do perfil"
 * e uma decisao sobre permissao, e "nao construido" e um fato sobre o sistema.
 * Mostrar manutencao como acesso negado a quem nunca vai ter manutencao para
 * abrir e desonesto, e ainda por cima manda a conversa para o lado errado.
 *
 * Nao e `useQuery` porque nao ha o que esperar: e leitura sincrona de memoria,
 * pelo mesmo motivo que `sessao/consultas.ts`.
 */

export interface PerfilNaProposta {
  readonly id: IdPerfil;
  readonly perfil: PerfilDisponivel;
}

export function perfilNaProposta(id: IdPerfil): PerfilNaProposta {
  return { id, perfil: perfilPorId(id) };
}

/** Um modulo que o perfil ve, com a derivacao que o colocou ali. */
export interface ModuloAlcancado {
  readonly modulo: ModuloDoSistema;
  readonly porque: string;
  readonly construido: boolean;
}

/** Uma acao do Sprint 1 concedida, com a derivacao que a concedeu. */
export interface AcaoConcedida {
  readonly acao: AcaoDoSprint;
  readonly porque: string;
}

export interface AlcanceDoPerfil {
  readonly perfilDaProposta: PerfilNaProposta;
  readonly modulos: readonly ModuloAlcancado[];
  readonly leitura: readonly AcaoConcedida[];
  readonly escrita: readonly AcaoConcedida[];
  /** Acoes do Sprint 1 que a proposta NAO concede a este perfil. */
  readonly acoesForaDoPerfil: readonly AcaoDoSprint[];
  /** Modulos que existem e que este perfil nao ve. Decisao de permissao. */
  readonly modulosForaDoPerfil: readonly ModuloDoSistema[];
  /** Modulos que este perfil nao ve e que tambem nao existem. Fato, nao permissao. */
  readonly modulosNaoConstruidos: readonly ModuloDoSistema[];
  /** O que a necessidade do perfil pede e o Sprint 1 nao tem. */
  readonly naoTemAcao: readonly string[];
  /** O primeiro modulo construido deste perfil. `null` quando nao ha nenhum. */
  readonly primeiroCaminho: CaminhoConstruido | null;
}

/* `-1` nao acontece: `IdModulo` e `IdAcao` saem das proprias listas, e
   `moduloPorId` e `acaoPorId` reprovam id de fora delas logo abaixo. */
function indiceDoModulo(id: IdModulo): number {
  return MODULOS_DO_SISTEMA.findIndex((modulo) => modulo.id === id);
}

function indiceDaAcao(id: IdAcao): number {
  return ACOES_DO_SPRINT.findIndex((acao) => acao.id === id);
}

export function alcanceDoPerfil(id: IdPerfil): AlcanceDoPerfil {
  const proposta = alcancePropostoDe(id);

  /* TODA LISTA DESTA RESPOSTA SAI NA ORDEM DE `MODULOS_DO_SISTEMA` E DE
     `ACOES_DO_SPRINT`, e nunca na ordem em que a proposta foi escrita. Sao
     duas contas diferentes e as duas importam:

      - comparar dois perfis em seguida, que e o uso da tela, vira caca ao item
        que mudou de lugar se cada perfil ordenar do seu jeito;
      - a lista de modulos e a que a tela promete que vira o menu, e o menu sai
        na ordem do mapa. Almoxarifado aparecia aqui como Pecas, Ordens de
        servico, Combustivel — e na navegacao como Combustivel, Ordens de
        servico, Pecas.

     A derivacao continua colada em cada item: o que muda e a ordem, nao o
     vinculo. */
  const modulos: readonly ModuloAlcancado[] = [...proposta.modulos]
    .sort((um, outro) => indiceDoModulo(um.id) - indiceDoModulo(outro.id))
    .map((escolhido) => {
      const modulo = moduloPorId(escolhido.id);
      return { modulo, porque: escolhido.porque, construido: moduloEstaConstruido(modulo) };
    });

  const concedidas: readonly AcaoConcedida[] = [...proposta.acoes]
    .sort((uma, outra) => indiceDaAcao(uma.id) - indiceDaAcao(outra.id))
    .map((escolhida) => ({ acao: acaoPorId(escolhida.id), porque: escolhida.porque }));

  const idsConcedidos = new Set(proposta.acoes.map((acao) => acao.id));
  const idsVistos = new Set(proposta.modulos.map((modulo) => modulo.id));

  const foraDoPerfil = MODULOS_DO_SISTEMA.filter((modulo) => !idsVistos.has(modulo.id));

  return {
    perfilDaProposta: perfilNaProposta(id),
    modulos,
    leitura: concedidas.filter((concedida) => concedida.acao.tipo === 'ler'),
    escrita: concedidas.filter((concedida) => concedida.acao.tipo === 'escrever'),
    acoesForaDoPerfil: ACOES_DO_SPRINT.filter((acao) => !idsConcedidos.has(acao.id)),
    modulosForaDoPerfil: foraDoPerfil.filter((modulo) => moduloEstaConstruido(modulo)),
    modulosNaoConstruidos: foraDoPerfil.filter((modulo) => !moduloEstaConstruido(modulo)),
    naoTemAcao: proposta.naoTemAcao,
    primeiroCaminho: primeiroCaminhoConstruido(modulos),
  };
}

function primeiroCaminhoConstruido(
  modulos: readonly ModuloAlcancado[],
): CaminhoConstruido | null {
  for (const alcancado of modulos) {
    if (alcancado.modulo.para !== undefined) return alcancado.modulo.para;
  }
  return null;
}

/**
 * A PERMISSAO QUE ABRE CADA MODULO saiu daqui (20/10/2026): era uma segunda
 * lista, uma licenca por modulo, ao lado da que a rota guarda em cada tela — e
 * as duas divergiam (o operador le a inspecao e o modulo dela pedia `os.ler`).
 * A fonte unica e `LEITURA_DA_TELA` (`./telas`): o modulo abre quando a tela
 * principal dele abre. O MENU vai alem e mostra o modulo quando QUALQUER tela
 * dele abre (`modulosDoMenu`, em `src/componentes/secoes.ts`).
 */

/**
 * O menu mostra o que a pessoa CONSEGUE ABRIR — e nao o que a proposta acha.
 *
 * ## O que mudou, e por que
 *
 * Antes esta funcao lia `alcancePropostoDe(perfil)`, isto e, a derivacao da
 * secao 1.4 do guia que mora em `proposta.ts`. Isso funcionava enquanto o
 * servidor nao conferia nada: o recorte era conveniencia de tela, e a propria
 * tela dizia isso por escrito.
 *
 * Com o portao de `backend/src/autorizacao/portao.ts` no ar, passaram a existir
 * DUAS derivacoes da mesma coisa: a proposta, aqui, e as linhas de
 * `org.papel_permissao`, la. Elas concordam hoje porque foram derivadas da
 * mesma tabela do guia — e divergem na primeira concessao que alguem alterar
 * pela tela de governanca, que e justamente a operacao que o sistema oferece.
 * O sintoma da divergencia e o pior que um menu pode ter: item que abre e
 * responde 403, ou acao que existe e some sem explicacao.
 *
 * Agora a fonte e uma so: as permissoes que a sessao devolve, que sao as mesmas
 * que o portao confere.
 *
 * ## Modulo que nao existe aparece para todo mundo, e isso e deliberado
 *
 * `operadores` nao tem rota, entao nao tem permissao para conferir.
 * `relatorios` (o painel) TEM rota e nao tem permissao propria: ele exige so a
 * sessao, e cada secao dele obedece a permissao do modulo de origem. Esconde-los
 * por perfil exigiria voltar a usar a proposta como se fosse regra — trocar um
 * problema pelo mesmo problema. Eles aparecem, marcados como nao construidos
 * pela propria navegacao, e no dia em que nascerem ganham permissao e entram no
 * mapa acima.
 *
 * ## `null` e `[]` sao ESTADOS DIFERENTES, e confundi-los foi um defeito
 *
 * A primeira versao recebia `readonly string[]` e tratava lista vazia como
 * "nao sei o que mostrar", devolvendo o mapa inteiro. Isso estava certo
 * enquanto o servidor nao barrava nada — e virou defeito no mesmo dia em que
 * ele passou a barrar: quem tem ZERO permissao veria o menu completo, e cada
 * item responderia 403. Ou seja, exatamente o "menu que mente" que esta funcao
 * foi reescrita para acabar.
 *
 * Agora a distincao e explicita:
 *
 *   `null` — a sessao ainda nao chegou. Mapa inteiro, para o menu nao piscar
 *            vazio no primeiro quadro e depois encher.
 *   `[]`   — a sessao chegou e a pessoa nao tem permissao nenhuma. O recorte
 *            VALE: sobram os modulos nao construidos, que nao tem permissao
 *            para conferir. Um menu quase vazio e desconfortavel e verdadeiro;
 *            um menu cheio de itens que recusam e confortavel e falso.
 *
 * Quem cai nesse caso descobre o motivo em Meu perfil, que diz o que pedir —
 * e essa tela nao exige permissao, justamente por isso.
 */
export function modulosVisiveis(permissoes: readonly string[] | null): readonly IdModulo[] {
  if (permissoes === null) return MODULOS_DO_SISTEMA.map((modulo) => modulo.id);
  const tem = new Set(permissoes);
  return MODULOS_DO_SISTEMA.filter((modulo) => {
    if (modulo.para === undefined) return true;
    const licencas = licencasDaTela(modulo.para);
    return licencas.length === 0 || licencas.some((licenca) => tem.has(licenca));
  }).map((modulo) => modulo.id);
}

/**
 * O que a tela escreve sobre um modulo que o perfil ve e que nao existe.
 *
 * Quando o repositorio registra o ciclo, ele entra; quando nao registra, a
 * frase DIZ que nao registra, em vez de inventar um numero de ciclo. O guia de
 * construcao tem essa informacao e ela nao foi trazida para ca — isso e
 * pendencia, nao licenca para chutar.
 */
export function notaDeModuloNaoConstruido(modulo: ModuloDoSistema): string {
  return modulo.ciclo === undefined
    ? 'Não construído. O ciclo em que entra está no guia de construção e não foi registrado neste repositório.'
    : `Não construído. ${modulo.ciclo}.`;
}
